import { MessageSquarePlus, PanelLeft } from "lucide-react";
import { useCallback } from "react";

import { cn } from "../../lib/utils";
import { relativeTime } from "../../lib/relative-time";

import type { AgentConversationSummary } from "../../lib/advisor-history";

// SILL-247: chat history & new-conversation flow.
//
// Toggleable sidebar that lists prior conversations newest-first. Click an
// entry → resume + re-hydrate. The sidebar replaces what would otherwise be
// a separate "History" button + drawer; persistent surface keeps the
// affordance discoverable instead of buried behind another click.
//
// Disabled-during-streaming rationale (applies to both sidebar entries AND
// the New chat button in the parent header): in-flight WebSocket frames
// target whichever conversation is currently `latest active` server-side.
// Resume + loadHistory swaps the displayed messages, so a `text_delta` for
// the previous conversation would corrupt the resumed conversation's view.
// New-chat has the same risk: clearMessages() then a fresh stream, but
// frames for the previous turn keep arriving.
//
// A future ticket can lift this restriction by stamping `conversationId` on
// every server frame and routing them in the store, but that's a wider
// blast-radius change touching the typed frame contract.

export interface AdvisorConversationSidebarProps {
  conversations: AgentConversationSummary[];
  /** ID of the conversation currently displayed in the chat surface. */
  activeConversationId?: string | null;
  /** Streaming gates clicks — see the file-level comment for why. */
  isStreaming: boolean;
  /** Collapsed = clipped to the right-edge toggle, with content still mounted. */
  isCollapsed: boolean;
  onToggle: () => void;
  /** Fires when the user wants to resume a different conversation. */
  onResume: (conversationID: string) => void;
  /** Fires when the user clicks New chat (also lives in the header). */
  onNewChat: () => void;
  /**
   * Initial-load skeleton — true when the parent has not yet fetched the
   * conversation list. Distinguish from "empty list" (fetch returned []).
   */
  isLoading?: boolean;
}

export function AdvisorConversationSidebar({
  conversations,
  activeConversationId,
  isStreaming,
  isCollapsed,
  onToggle,
  onResume,
  onNewChat,
  isLoading = false,
}: AdvisorConversationSidebarProps) {
  const handleResumeClick = useCallback(
    (id: string) => () => {
      if (isCollapsed || isStreaming) return;
      onResume(id);
    },
    [isCollapsed, isStreaming, onResume],
  );

  return (
    <aside
      aria-label="Chat history"
      className={cn(
        "relative flex h-full w-[280px] flex-col overflow-hidden border-r border-border/60 bg-bg-warm motion-safe:transition-transform motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
        isCollapsed && "-translate-x-[236px]",
      )}
      data-collapsed={isCollapsed ? "true" : "false"}
      data-slot="advisor-conversation-sidebar"
    >
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-0 right-9 z-20 w-4 bg-gradient-to-r from-bg-warm via-bg-warm/90 to-bg-warm/0 transition-opacity duration-150",
          isCollapsed ? "opacity-100" : "opacity-0",
        )}
        data-slot="advisor-sidebar-collapsed-mask"
      />
      <div
        className="sticky top-0 z-10 shrink-0 bg-bg-warm pt-3"
        data-slot="advisor-conversation-sidebar-header"
      >
        <div
          className="flex min-h-7 items-center justify-between gap-2 px-2 pb-1"
          data-slot="advisor-sidebar-title-row"
        >
          <p className="whitespace-nowrap text-sm font-semibold text-foreground transition-opacity duration-150">
            Chats
          </p>
          <button
            aria-label={
              isCollapsed ? "Open chat history" : "Collapse chat history"
            }
            aria-expanded={!isCollapsed}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[background-color,color,transform,opacity] hover:bg-muted/60 hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            data-slot="advisor-sidebar-toggle"
            onClick={onToggle}
            title={isCollapsed ? "Open chat history" : "Collapse chat history"}
            type="button"
          >
            <PanelLeft aria-hidden className="h-4 w-4" />
          </button>
        </div>
        <button
          className={cn(
            "flex min-h-10 w-full items-center gap-1 rounded-md text-xs font-medium text-muted-foreground transition-[background-color,color,opacity] hover:bg-muted/60 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            isCollapsed
              ? "flex-row-reverse justify-start px-2"
              : "justify-start px-2",
          )}
          data-slot="advisor-sidebar-new-chat"
          disabled={isStreaming}
          onClick={onNewChat}
          title={
            isStreaming ? "Wait for the current response to finish." : undefined
          }
          type="button"
        >
          <span
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center"
            data-slot="advisor-sidebar-new-chat-icon"
          >
            <MessageSquarePlus aria-hidden className="h-4 w-4" />
          </span>
          <span
            className={cn(
              "whitespace-nowrap transition-opacity duration-150",
              isCollapsed ? "opacity-0" : "opacity-100",
            )}
            data-slot="advisor-sidebar-new-chat-label"
          >
            New chat
          </span>
        </button>
        <div
          aria-hidden
          className={cn(
            "mt-2 w-full border-t transition-colors duration-150",
            isCollapsed ? "border-transparent" : "border-border/60",
          )}
          data-slot="advisor-sidebar-new-chat-divider"
        />
      </div>

      <div
        aria-hidden={isCollapsed ? "true" : undefined}
        className={cn(
          "flex min-h-0 flex-1 flex-col transition-opacity duration-150",
          isCollapsed ? "pointer-events-none opacity-0" : "opacity-100",
        )}
        data-slot="advisor-sidebar-history-content"
      >
        <div
          className="min-h-0 flex-1 overflow-y-auto"
          data-slot="advisor-conversation-sidebar-list"
        >
          {isLoading ? (
            <SidebarSkeleton />
          ) : conversations.length === 0 ? (
            <SidebarEmptyState />
          ) : (
            <ul className="space-y-0.5 px-1.5 pb-3">
              {conversations.map((c) => {
                const isActive = c.id === activeConversationId;
                return (
                  <li key={c.id}>
                    <button
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "group block w-full rounded-md px-2 py-1.5 text-left transition-colors",
                        "hover:bg-muted/60",
                        isActive && "bg-muted/80",
                        (isCollapsed || (isStreaming && !isActive)) &&
                          "cursor-not-allowed opacity-60",
                      )}
                      data-active={isActive ? "true" : undefined}
                      data-slot="advisor-sidebar-entry"
                      disabled={isCollapsed || (isStreaming && !isActive)}
                      onClick={handleResumeClick(c.id)}
                      tabIndex={isCollapsed ? -1 : undefined}
                      title={
                        isStreaming && !isActive
                          ? "Wait for the current response to finish."
                          : undefined
                      }
                      type="button"
                    >
                      <div className="truncate text-xs font-medium text-foreground">
                        {labelForConversation(c)}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                        <span>{relativeTime(c.last_message_at)}</span>
                        <span aria-hidden>·</span>
                        <span>
                          {c.message_count}{" "}
                          {c.message_count === 1 ? "message" : "messages"}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </aside>
  );
}

function SidebarEmptyState() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-1 px-3 py-8 text-center"
      data-slot="advisor-sidebar-empty"
    >
      <p className="text-sm font-medium text-foreground">No chats yet</p>
      <p className="text-xs font-medium text-muted-foreground">
        Start a conversation to see it here.
      </p>
    </div>
  );
}

function SidebarSkeleton() {
  return (
    <div className="space-y-1.5 px-3 py-2" data-slot="advisor-sidebar-skeleton">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-10 animate-pulse rounded-md bg-muted-foreground/10"
        />
      ))}
    </div>
  );
}

// labelForConversation falls back to a friendly placeholder when the title
// hasn't been generated yet — brand-new conversations with zero turns hit
// this until the first user message lands and Orchestrator.MaybeSetTitle
// stamps the row.
function labelForConversation(c: AgentConversationSummary): string {
  if (c.title && c.title.trim() !== "") return c.title;
  return "New chat";
}

